/**
 * RoutePriceEstimate.js — Fairness-Richtpreis-Block für RideSearch
 * ─────────────────────────────────────────────────────────────────
 * Zeigt auf der Such-Seite, was eine Mitfahrt fair kosten sollte:
 *   • Max-Preis (nur 1 Mitfahrer → Fahrer teilt 1:1 mit Passagier)
 *   • Staffel bei 2 / 3 / 4 Mitfahrern → Preis pro Person sinkt
 *
 * Annahmen (ADAC Pendler-Richtwert):
 *   • Vollkosten = 0,30 €/km (Benzin + Abschreibung + Versicherung)
 *   • Fahrer trägt IMMER den Fahrer-Anteil (totalCost / seatsIncludingDriver)
 *
 * Props:
 *   fromCoords {lat, lng} | null
 *   toCoords   {lat, lng} | null
 *   minDistanceKm  (default 5)   — darunter wird nichts gezeigt
 */
import React from 'react';
import { TrendingDown, Users, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const VOLLKOSTEN_PER_KM = 0.30;  // ADAC Richtwert für Pendler-Vollkosten

function haversineKm(a, b) {
  if (!a || !b) return 0;
  const R = 6371;
  const toRad = (v) => (v * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const la1 = toRad(a.lat);
  const la2 = toRad(b.lat);
  const x = Math.sin(dLat / 2) ** 2
          + Math.sin(dLng / 2) ** 2 * Math.cos(la1) * Math.cos(la2);
  return 2 * R * Math.asin(Math.sqrt(x));
}

export const RoutePriceEstimate = ({ fromCoords, toCoords, minDistanceKm = 5 }) => {
  const { t } = useTranslation();
  const distanceKm = haversineKm(fromCoords, toCoords);
  if (!distanceKm || distanceKm < minDistanceKm) return null;

  const totalCost = distanceKm * VOLLKOSTEN_PER_KM;
  const tiers = [1, 2, 3, 4].map((seats) => ({
    seats,
    // Fahrer + seats passengers teilen sich total
    pricePerPassenger: totalCost / (seats + 1),
  }));
  const maxPrice = tiers[0].pricePerPassenger;

  return (
    <div data-testid="route-price-estimate" style={s.card}>
      <div style={s.header}>
        <div style={s.iconBox}>
          <TrendingDown size={16} color="#22c55e" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={s.title}>
            {t('priceEstimate.title', 'Fairness-Richtpreis')}
            <span style={s.distance}>≈ {distanceKm.toFixed(1)} km</span>
          </div>
          <div style={s.subtitle}>
            {t('priceEstimate.subtitle', 'Bei mehr Mitfahrern sinkt dein Preis')}
          </div>
        </div>
        <div style={s.maxBox} data-testid="price-max">
          <span style={s.maxLabel}>{t('priceEstimate.max', 'Höchstpreis')}</span>
          <span style={s.maxValue}>{maxPrice.toFixed(2)} €</span>
        </div>
      </div>

      <div style={s.tierRow}>
        {tiers.map(({ seats, pricePerPassenger }, idx) => {
          const discount = idx === 0 ? 0 : Math.round((1 - pricePerPassenger / maxPrice) * 100);
          return (
            <div key={seats}
                 data-testid={`price-tier-${seats}`}
                 style={{ ...s.tier, ...(idx === 0 ? s.tierBase : {}) }}>
              <div style={s.tierHeader}>
                <Users size={11} />
                <span>{seats} {seats === 1 ? t('priceEstimate.passenger', 'Mitfahrer') : t('priceEstimate.passengers', 'Mitfahrer')}</span>
              </div>
              <div style={s.tierPrice}>{pricePerPassenger.toFixed(2)} €</div>
              {discount > 0 && (
                <div style={s.tierDiscount}>−{discount}%</div>
              )}
            </div>
          );
        })}
      </div>

      <div style={s.footer}>
        <Info size={11} />
        <span>
          {t('priceEstimate.hint',
            'ADAC-Vollkostenrichtwert (0,30 €/km) geteilt durch alle Insassen inkl. Fahrer.')}
        </span>
      </div>
    </div>
  );
};

export default RoutePriceEstimate;

// ── Styles (Glassmorphism, passt zu RideSearch) ──────────────────
const s = {
  card: {
    background: 'rgba(15,23,42,0.85)',
    backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
    borderWidth: 1, borderStyle: 'solid',
    borderColor: 'rgba(34,197,94,0.25)',
    borderRadius: 14,
    padding: 16,
    marginTop: 14, marginBottom: 14,
    color: '#f1f5f9',
    display: 'flex', flexDirection: 'column', gap: 12,
    boxShadow: '0 10px 28px rgba(0,0,0,0.28)',
  },
  header: { display: 'flex', alignItems: 'center', gap: 12 },
  iconBox: {
    width: 34, height: 34, borderRadius: 10, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(34,197,94,0.15)',
    border: '1px solid rgba(34,197,94,0.3)',
  },
  title: {
    display: 'flex', alignItems: 'center', gap: 10,
    fontSize: 14, fontWeight: 700, color: '#f1f5f9',
  },
  distance: {
    fontSize: 11, fontWeight: 600,
    padding: '2px 8px', borderRadius: 999,
    background: 'rgba(255,255,255,0.06)', color: '#94a3b8',
    border: '1px solid rgba(255,255,255,0.08)',
  },
  subtitle: { fontSize: 12, color: '#94a3b8', marginTop: 2 },
  maxBox: {
    textAlign: 'right', display: 'flex', flexDirection: 'column',
  },
  maxLabel: { fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 },
  maxValue: { fontSize: 18, fontWeight: 800, color: '#bbf7d0' },
  tierRow: {
    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8,
  },
  tier: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 10, padding: '8px 6px',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
    position: 'relative',
  },
  tierBase: {
    background: 'rgba(239,68,68,0.08)',
    borderColor: 'rgba(239,68,68,0.25)',
  },
  tierHeader: {
    display: 'flex', alignItems: 'center', gap: 3,
    fontSize: 10, color: '#94a3b8', fontWeight: 600,
  },
  tierPrice: { fontSize: 15, fontWeight: 700, color: '#f1f5f9' },
  tierDiscount: {
    fontSize: 10, fontWeight: 700, color: '#22c55e',
    padding: '1px 6px', borderRadius: 999,
    background: 'rgba(34,197,94,0.12)',
    border: '1px solid rgba(34,197,94,0.3)',
  },
  footer: {
    display: 'flex', alignItems: 'center', gap: 6,
    fontSize: 10, color: '#64748b',
    paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.05)',
  },
};
